import { throwError as observableThrowError, Observable } from 'rxjs';

import { catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpBackend } from '@angular/common/http';
import { Router } from '@angular/router';

import { environment } from '@myenv/environment';

export interface GroupAddDevicesPayload {
  groupName: string;
  serialNumbers: string[];
}

export interface CreateGroupWithDevicesOptions {
  groupName: string;
  devices?: string[];
  csvFile?: File | null;
  allDevices?: boolean;
}

@Injectable()
export class GroupsService {

  public apiUrl = environment.apiUrl;
  public apiV2Url = environment.apiV2Url;
  private httpClient: HttpClient;
  public type!: string;
  public customerId!: Number;

  constructor(
    private http: HttpClient,
    private router: Router,
    private handler: HttpBackend,
  ) {
    this.httpClient = new HttpClient(handler);
  }

  private getCustomerContext(): { type: string; customerId: Number } {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    this.type = String(user.productType || '').toLowerCase();
    this.customerId = user.customerId;
    return { type: this.type, customerId: this.customerId };
  }

  private authHeaders(json = false): HttpHeaders {
    const headers: Record<string, string> = {
      Authorization: 'Bearer ' + (localStorage.getItem('token') || ''),
    };
    if (json) {
      headers['Content-Type'] = 'application/json';
    }
    return new HttpHeaders(headers);
  }

  private v2GroupsBase(): string {
    const { type, customerId } = this.getCustomerContext();
    return `${this.apiV2Url}${type}/${customerId}/v2/groups`;
  }

  // To get groups
  getGroups() {
    const headers = this.authHeaders(true);

    return this.http.get(this.apiUrl + 'groups?includeDevice=true', { headers }).pipe(
      catchError(this.handleError));
  }

  // To get assigned groups
  getAssignedGroups() {
    const headers = this.authHeaders(true);

    return this.http.get(this.apiUrl + 'groups?includeDevice=true', { headers }).pipe(
      catchError(this.handleError));
  }

  // To create a group
  createGroup(data: any) {
    const headers = this.authHeaders(true);

    return this.http.post(this.apiUrl + 'groups', data, { headers }).pipe(
      catchError(this.handleError));
  }

  createGroupWithDevices(options: CreateGroupWithDevicesOptions): Observable<any> {
    const formData = new FormData();
    formData.append(
      'group',
      JSON.stringify({ name: String(options.groupName ?? '').trim() }),
    );

    if (options.devices?.length) {
      formData.append('devices', JSON.stringify(options.devices));
    }

    if (options.csvFile) {
      formData.append('csvFile', options.csvFile);
    }

    formData.append('allDevices', options.allDevices ? 'True' : 'False');

    return this.httpClient
      .post(this.v2GroupsBase() + '/create', formData, {
        headers: this.authHeaders(false),
      })
      .pipe(catchError(this.handleError));
  }

  // To update a group
  updateGroup(data: any) {
    return this.http.put(this.apiUrl + 'groups/' + data.groupId + '?groupName=' + data.name, data).pipe(
      catchError(this.handleError));
  }

  updateGroupName(groupId: string, groupName: string): Observable<any> {
    const encodedName = encodeURIComponent(groupName);
    return this.http
      .put(
        `${this.v2GroupsBase()}/${encodeURIComponent(groupId)}?groupName=${encodedName}`,
        {},
        { headers: this.authHeaders(true) },
      )
      .pipe(catchError(this.handleError));
  }

  // To delete a group
  deleteGroup(groupId: string) {
    const headers = this.authHeaders(true);

    return this.http.delete(this.apiUrl + 'groups/' + groupId, { headers }).pipe(
      catchError(this.handleError));
  }

  deleteGroupById(groupId: string): Observable<any> {
    return this.http
      .delete(`${this.v2GroupsBase()}/${encodeURIComponent(groupId)}`, {
        headers: this.authHeaders(true),
      })
      .pipe(catchError(this.handleError));
  }

  deleteGroupByName(groupName: string): Observable<any> {
    return this.http
      .delete(this.v2GroupsBase() + '/delete', {
        headers: this.authHeaders(true),
        body: { groupName },
      })
      .pipe(catchError(this.handleError));
  }

  onSearchGroup(value: any, includeDevice: any): Observable<any> {
    return this.http.get(`${this.apiUrl}groups?name=${value}&includeDevice=${includeDevice}`).pipe(
      catchError(this.handleError));
  }

  handleError(error: any | any) {
    return observableThrowError(error.error || error.json() || error || 'Server error');
  }

  getAssociatedVehicles(groupId: string, page: number, pageLimit: any) {
    return this.http.get(
      this.apiUrl +
      `devices?sortingKey=lastCommunication&direction=desc&key=groupName&value=${groupId}&attributes=assetName&page=${page}&pagelimit=${pageLimit}&isAssociatedDevice=true`)
      .pipe(catchError(this.handleError));
  }

  addDevicesToGroup(groupId: string, devices: any, selectAll: any, filterCriteria: any) {
    let payload: any = { actionName: 'CG', selectAll: selectAll, deviceIds: devices, groupId: groupId };
    if (selectAll) {
      payload.filterCriteria = {};
      if (filterCriteria[1]) {
        payload.filterCriteria[filterCriteria[0]] = filterCriteria[1];
      }
      payload.filterCriteria['isAssociatedDevice'] = false;
      if (filterCriteria[3]) {
        payload.filterCriteria['groupId'] = filterCriteria[3];
      } if (filterCriteria[4]) {
        payload.filterCriteria['status'] = filterCriteria[4];
      }
    }
    return this.http.put(this.apiUrl + 'devices/bulkAction', payload).pipe(
      catchError(this.handleError));
  }

  addDevicesToGroupBySerials(payload: GroupAddDevicesPayload): Observable<any> {
    return this.http
      .put(this.v2GroupsBase() + '/addDevices', payload, {
        headers: this.authHeaders(true),
      })
      .pipe(catchError(this.handleError));
  }

  addDevicesToTag(id: string, devices: any, selectAll: any, action: any, filterCriteria: any) {
    let payload: any = { actionName: action, selectAll: selectAll, deviceIds: devices, tags: [id] };
    if (selectAll) {
      payload.filterCriteria = {};
      if (filterCriteria[1]) {
        payload.filterCriteria[filterCriteria[0]] = filterCriteria[1];
      }
      payload.filterCriteria['isAssociatedDevice'] = false;
      if (filterCriteria[3]) {
        payload.filterCriteria['groupId'] = filterCriteria[3];
      } if (filterCriteria[4]) {
        payload.filterCriteria['status'] = filterCriteria[4];
      }
    }
    return this.http.put(this.apiUrl + 'devices/bulkAction', payload).pipe(
      catchError(this.handleError));
  }
}
